import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiUrl, apiUnreachableMessage } from '../config/api';
import { getStoredUser } from '../utils/auth';
import { CameraCaptureModal } from './FormSiteTimestampControls';

const inputClass =
  'w-full px-3 py-2 rounded-md bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600';
const labelClass = 'block text-sm font-medium text-gray-300 mb-1';

function authHeaders() {
  const user = getStoredUser();
  return { 'X-User-Email': user?.email || '' };
}

function formatDate(value) {
  if (!value) return '—';
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString();
}

function normalizeImageUrls(item) {
  if (Array.isArray(item?.image_urls) && item.image_urls.length) {
    return item.image_urls.filter(Boolean);
  }
  return item?.image_url ? [item.image_url] : [];
}

/** Short display name: keep start + extension, e.g. long-name….png */
function shortenFileName(name, maxLen = 28) {
  const text = String(name || '').trim();
  if (!text || text.length <= maxLen) return text;
  const lastDot = text.lastIndexOf('.');
  const ext = lastDot > 0 && lastDot < text.length - 1 ? text.slice(lastDot) : '';
  const base = ext ? text.slice(0, lastDot) : text;
  const budget = Math.max(6, maxLen - ext.length - 1);
  return `${base.slice(0, budget)}…${ext}`;
}

export function HazardsAndControlPanel({ onBack }) {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [hazards, setHazards] = useState([]);
  const [hazardText, setHazardText] = useState('');
  const [controlText, setControlText] = useState('');
  const [hazardDate, setHazardDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [controlDate, setControlDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [imageFiles, setImageFiles] = useState([]);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const galleryInputRef = useRef(null);
  const uploadMenuRef = useRef(null);
  const currentUser = getStoredUser();

  useEffect(() => {
    if (!showUploadMenu) return undefined;
    const handlePointerDown = (event) => {
      if (!uploadMenuRef.current?.contains(event.target)) setShowUploadMenu(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [showUploadMenu]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/admin/sites'), { headers: authHeaders() });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(data.detail || 'Failed to load sites');
          return;
        }
        const nextSites = Array.isArray(data.sites) ? data.sites : [];
        setSites(nextSites);
        if (nextSites.length && !siteId) setSiteId(String(nextSites[0].id));
      } catch {
        if (!cancelled) setError(apiUnreachableMessage());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const loadHazards = async (nextSiteId) => {
    if (!nextSiteId) {
      setHazards([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(apiUrl(`/api/hazards?site_id=${nextSiteId}`), {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to load hazards');
        return;
      }
      setHazards(Array.isArray(data.hazards) ? data.hazards : []);
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHazards(siteId);
  }, [siteId]);

  const grouped = useMemo(() => {
    const groups = {};
    hazards.forEach((item) => {
      const key = item.hazard_date || String(item.created_at || '').slice(0, 10) || 'Undated';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return Object.entries(groups);
  }, [hazards]);

  const resetForm = () => {
    setHazardText('');
    setControlText('');
    setHazardDate(new Date().toISOString().slice(0, 10));
    setControlDate(new Date().toISOString().slice(0, 10));
    setImageFiles([]);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const appendImageFiles = (nextFiles) => {
    const list = Array.from(nextFiles || []).filter(Boolean);
    if (!list.length) return;
    setImageFiles((prev) => [...prev, ...list]);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!siteId) {
      setError('Choose a site first');
      return;
    }
    if (!hazardText.trim()) {
      setError('Describe the hazard');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = new FormData();
      body.append('site_id', siteId);
      body.append('hazard_text', hazardText.trim());
      body.append('control_text', controlText.trim());
      if (hazardDate) body.append('hazard_date', hazardDate);
      if (controlDate) body.append('control_date', controlDate);
      imageFiles.forEach((file) => body.append('images', file));
      const res = await fetch(apiUrl('/api/hazards'), {
        method: 'POST',
        headers: authHeaders(),
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to save hazard');
        return;
      }
      resetForm();
      await loadHazards(siteId);
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(apiUrl(`/api/hazards/${item.id}`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to remove hazard');
        return;
      }
      await loadHazards(siteId);
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setSaving(false);
    }
  };

  const selectedSite = sites.find((site) => String(site.id) === String(siteId));

  return (
    <div className="max-w-none w-full">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-400 hover:text-white mb-3"
        >
          ← Back to dashboard
        </button>
      ) : null}
      <h2 className="text-xl font-semibold text-white mb-0.5">Hazards and Control</h2>
      <p className="text-gray-400 text-xs mb-4">
        Add hazards per site with photos and the control used. Entries are listed by date.
      </p>

      {error ? (
        <div className="text-red-400 text-sm bg-red-400/10 py-1.5 px-3 rounded mb-3">{error}</div>
      ) : null}

      <div className="mb-4 max-w-md">
        <label htmlFor="hazard-site" className={labelClass}>
          Site / Project
        </label>
        <select
          id="hazard-site"
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          className={inputClass}
        >
          {!sites.length ? <option value="">No sites assigned</option> : null}
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.name}
              {site.company_name ? ` · ${site.company_name}` : ''}
            </option>
          ))}
        </select>
      </div>

      <form
        onSubmit={handleSave}
        className="grid max-w-md w-full min-w-0 gap-3 border border-gray-800 rounded-lg p-3 mb-5 bg-gray-950/60 overflow-hidden"
      >
        <div>
          <label className={labelClass}>What is this hazard?</label>
          <textarea
            value={hazardText}
            onChange={(e) => setHazardText(e.target.value)}
            rows={3}
            className={inputClass}
            placeholder="Describe the hazard"
          />
        </div>
        <div>
          <label className={labelClass}>Control</label>
          <textarea
            value={controlText}
            onChange={(e) => setControlText(e.target.value)}
            rows={3}
            className={inputClass}
            placeholder="Write how this hazard is controlled"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Hazard date</label>
            <input
              type="date"
              value={hazardDate}
              onChange={(e) => setHazardDate(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Control date</label>
            <input
              type="date"
              value={controlDate}
              onChange={(e) => setControlDate(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <div className="min-w-0 overflow-hidden">
          <label className={labelClass}>Photos</label>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <div className="relative shrink-0" ref={uploadMenuRef}>
              <button
                type="button"
                onClick={() => setShowUploadMenu((open) => !open)}
                className="inline-flex h-8 items-center rounded border border-sky-300/50 bg-sky-400/20 px-3 text-[11px] font-semibold text-sky-200 hover:bg-sky-400/30"
              >
                Upload / take picture
                {imageFiles.length ? (
                  <span className="ml-1.5 inline-flex min-w-[0.95rem] items-center justify-center rounded-full bg-sky-500/40 px-1 text-[9px] leading-none text-sky-100">
                    {imageFiles.length}
                  </span>
                ) : null}
              </button>
              {showUploadMenu ? (
                <div className="absolute left-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-md border border-gray-700 bg-gray-900 py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadMenu(false);
                      galleryInputRef.current?.click();
                    }}
                    className="flex w-full px-3 py-2 text-left text-xs font-semibold text-gray-200 hover:bg-gray-800"
                  >
                    Choose from gallery
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadMenu(false);
                      setShowCamera(true);
                    }}
                    className="flex w-full px-3 py-2 text-left text-xs font-semibold text-gray-200 hover:bg-gray-800"
                  >
                    Take picture
                  </button>
                </div>
              ) : null}
            </div>
            <span className="min-w-0 truncate text-[11px] text-gray-400">
              {imageFiles.length
                ? `${imageFiles.length} photo${imageFiles.length === 1 ? '' : 's'} selected`
                : 'No photos selected'}
            </span>
          </div>
          {imageFiles.length ? (
            <ul className="mt-2 max-h-28 min-w-0 space-y-1 overflow-y-auto overflow-x-hidden">
              {imageFiles.map((file, index) => (
                <li
                  key={`${file.name}-${file.size}-${index}`}
                  className="flex min-w-0 items-center justify-between gap-2 rounded border border-gray-800 bg-black/40 px-2 py-1"
                >
                  <span
                    className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-gray-300"
                    title={file.name}
                  >
                    {shortenFileName(file.name)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setImageFiles((prev) => prev.filter((_, i) => i !== index))}
                    className="shrink-0 text-xs font-semibold text-red-400 hover:text-red-300"
                    aria-label={`Remove ${file.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            className="hidden"
            onChange={(event) => {
              appendImageFiles(event.target.files);
              event.target.value = '';
            }}
          />
        </div>
        <div>
          <button
            type="submit"
            disabled={saving || !siteId}
            className="px-4 py-2 rounded-md bg-[#eda448] text-gray-900 text-sm font-semibold hover:bg-[#d8932e] disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Add hazard'}
          </button>
        </div>
      </form>

      <h3 className="text-sm font-semibold text-gray-300 mb-2">
        {selectedSite ? `Hazards for ${selectedSite.name}` : 'Hazards'}
      </h3>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !grouped.length ? (
        <p className="text-sm text-gray-500 border border-gray-800 rounded-md py-6 text-center">
          No hazards logged for this site yet.
        </p>
      ) : (
        <div className="space-y-4">
          {grouped.map(([dateKey, items]) => (
            <div key={dateKey} className="border border-gray-800 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-gray-900 text-xs font-semibold uppercase tracking-wide text-gray-400">
                {formatDate(dateKey)}
              </div>
              <ul className="divide-y divide-gray-800">
                {items.map((item) => {
                  const urls = normalizeImageUrls(item);
                  return (
                    <li key={item.id} className="p-3 flex gap-3">
                      {urls.length ? (
                        <div className="flex shrink-0 gap-1">
                          {urls.slice(0, 3).map((url) => (
                            <a
                              key={url}
                              href={apiUrl(url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0"
                            >
                              <img
                                src={apiUrl(url)}
                                alt=""
                                className="h-16 w-16 rounded object-cover border border-gray-700 sm:h-20 sm:w-20"
                              />
                            </a>
                          ))}
                          {urls.length > 3 ? (
                            <span className="flex h-16 w-10 items-center justify-center rounded border border-gray-700 text-[10px] text-gray-400 sm:h-20">
                              +{urls.length - 3}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <div className="h-20 w-20 shrink-0 rounded border border-dashed border-gray-700 text-[10px] text-gray-500 flex items-center justify-center">
                          No photo
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white whitespace-pre-wrap">{item.hazard_text}</p>
                        {item.control_text ? (
                          <p className="mt-1 text-xs text-gray-300 whitespace-pre-wrap">
                            <span className="text-gray-500">Control: </span>
                            {item.control_text}
                          </p>
                        ) : null}
                        <p className="mt-1 text-[11px] text-gray-500">
                          Hazard {formatDate(item.hazard_date)} · Control{' '}
                          {formatDate(item.control_date)}
                          {item.added_by ? ` · ${item.added_by}` : ''}
                        </p>
                      </div>
                      {item.user_id === currentUser?.id ||
                      currentUser?.role === 'admin' ||
                      currentUser?.role === 'super_admin' ||
                      currentUser?.role === 'super_user' ? (
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          disabled={saving}
                          className="shrink-0 text-red-400 hover:text-red-300 text-lg leading-none"
                          aria-label="Delete hazard"
                        >
                          ×
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {showCamera ? (
        <CameraCaptureModal
          multiple
          onCapture={(fileOrFiles) => {
            appendImageFiles(
              Array.isArray(fileOrFiles) ? fileOrFiles : fileOrFiles ? [fileOrFiles] : []
            );
            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      ) : null}
    </div>
  );
}

export default HazardsAndControlPanel;
